import React, { useState } from 'react';
import { MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPolicyContent = ({ t }) => (
  <div className="prose prose-sm max-w-none text-slate-600 space-y-4">
    <p className="text-sm text-slate-500">{t('legal.lastUpdated')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title1')}</h3>
    <p>{t('legal.privacy.text1')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title2')}</h3>
    <p>{t('legal.privacy.text2')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title3')}</h3>
    <p>{t('legal.privacy.text3')}</p>
    <ul className="list-disc pl-5 space-y-1">
      {t('legal.privacy.list3')?.map((item, index) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title4')}</h3>
    <p>{t('legal.privacy.text4')}</p>
    <ul className="list-disc pl-5 space-y-1">
      {t('legal.privacy.list4')?.map((item, index) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title5')}</h3>
    <p>{t('legal.privacy.text5')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title6')}</h3>
    <p>{t('legal.privacy.text6')}</p>
    <ul className="list-disc pl-5 space-y-1">
      {t('legal.privacy.list6')?.map((item, index) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title7')}</h3>
    <p>{t('legal.privacy.text7')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title8')}</h3>
    <p>{t('legal.privacy.text8')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.privacy.title9')}</h3>
    <p>{t('legal.privacy.text9')}</p>
  </div>
);

const TermsOfServiceContent = ({ t }) => (
  <div className="prose prose-sm max-w-none text-slate-600 space-y-4">
    <p className="text-sm text-slate-500">{t('legal.lastUpdated')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title1')}</h3>
    <p>{t('legal.terms.text1a')}</p>
    <p>{t('legal.terms.text1b')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title2')}</h3>
    <p>{t('legal.terms.text2')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title3')}</h3>
    <p>{t('legal.terms.text3')}</p>
    <ul className="list-disc pl-5 space-y-1">
      {t('legal.terms.list3')?.map((item, index) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title4')}</h3>
    <p>{t('legal.terms.text4')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title5')}</h3>
    <p>{t('legal.terms.text5')}</p>
    <ul className="list-disc pl-5 space-y-1">
      {t('legal.terms.list5')?.map((item, index) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title6')}</h3>
    <p>{t('legal.terms.text6a')}</p>
    <p>{t('legal.terms.text6b')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title7')}</h3>
    <p>{t('legal.terms.text7')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title8')}</h3>
    <p>{t('legal.terms.text8')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title9')}</h3>
    <p>{t('legal.terms.text9')}</p>
    
    <h3 className="text-lg font-semibold text-[#0d1442]">{t('legal.terms.title10')}</h3>
    <p>{t('legal.terms.text10')}</p>
  </div>
);

const Footer = () => {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const { t } = useLanguage();

  const quickLinks = [
    { href: '#services', label: t('nav.services') },
    { href: '#how-it-works', label: t('nav.howItWorks') },
    { href: '#case-studies', label: t('nav.caseStudies') },
    { href: '#contact', label: t('nav.contact') }
  ];

  return (
    <footer className="bg-slate-50 border-t border-slate-200 py-12">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-12">
          {/* Description */}
          <div className="col-span-2 md:col-span-1">
            <p className="text-[#0d1442] text-sm leading-relaxed font-bold">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#1a237e] mb-4">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href}
                    className="text-slate-600 text-sm hover:text-[#1a237e] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Region */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#1a237e] mb-4">
              {t('footer.serviceRegion')}
            </h3>
            <div className="flex items-start gap-2 text-slate-600 mb-6">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{t('footer.region')}</span>
            </div>

            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#1a237e] mb-4">
              {t('footer.offices')}
            </h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="https://maps.google.com/?q=Hlušičkova+16,+155+00+Prague,+Czechia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 text-sm hover:text-[#1a237e] transition-colors flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  {t('footer.prague')}
                </a>
              </li>
              <li>
                <a 
                  href="https://maps.google.com/?q=Metodova+8,+82108+Bratislava,+Slovakia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 text-sm hover:text-[#1a237e] transition-colors flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  {t('footer.bratislava')}
                </a>
              </li>
            </ul>
          </div>

          {/* Company Info */}
          <div>
            <p className="text-[#0d1442] text-sm leading-relaxed font-bold mb-4">
              BIZZ Europe a.s.<br />
              ID: 07649738<br />
              ID VAT: CZ07649738
            </p>
            <p className="text-slate-600 text-sm leading-relaxed">
              Korunní 2569/108,<br />
              101 00 Praha 10<br />
              Czech Republic
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} {t('footer.copyright')}
            </p>
            <div className="flex gap-6">
              <button 
                onClick={() => setShowPrivacyPolicy(true)}
                className="text-slate-500 text-sm hover:text-[#1a237e] transition-colors"
              >
                {t('footer.privacyPolicy')}
              </button>
              <button 
                onClick={() => setShowTermsOfService(true)}
                className="text-slate-500 text-sm hover:text-[#1a237e] transition-colors"
              >
                {t('footer.termsOfService')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <Dialog open={showPrivacyPolicy} onOpenChange={setShowPrivacyPolicy}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0d1442]">
              {t('footer.privacyPolicy')}
            </DialogTitle>
          </DialogHeader>
          <PrivacyPolicyContent t={t} />
        </DialogContent>
      </Dialog>

      {/* Terms of Service Modal */}
      <Dialog open={showTermsOfService} onOpenChange={setShowTermsOfService}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0d1442]">
              {t('footer.termsOfService')}
            </DialogTitle>
          </DialogHeader>
          <TermsOfServiceContent t={t} />
        </DialogContent>
      </Dialog>
    </footer>
  );
};

export default Footer;
